import CartClientV2, { removeCartItemImportant } from 'clients/CartClientV2';
import { getFirst, isValid, isValidWithoutData } from 'clients/Clients';
import { getCartBySeller } from 'clients/ProductClientV2';
import { getScreenCartCrossSellHelper, getScreenCartVoucherHelper } from 'clients/WebServiceClient';
import { CART_URL } from 'constants/Paths';
import Router from 'next/router';
import { convertCartStore, convertCrossSellItem } from 'services/ProductServiceV3';
import type { Product, Seller } from 'types/product';
import { CartStore, CrossSellItem, EnumVoucherSuggestStatus, IOldCartItem, IRedeemApplyResult, IVoucherSuggestions } from 'types/product';
import gtag from 'utils/gtag';
import { updateInsiderCartInfo } from 'utils/Insider';
import { mapScreenToEnum, mapSourceToEnum } from 'utils/MonitorUtils';
import NotifyUtils from 'utils/NotifyUtils';
import { capitalizeText } from 'utils/StringUtils';
import create from 'zustand';
import { devtools } from 'zustand/middleware';
import { loadDataCartLiteEncrypted } from '../clients/WebServiceClient';
import {
	CartParams,
	FETCH_STATUS,
	FetchStatus,
	ImportantProductPayload,
	Options,
	ProductUpdate,
	RemoveCartItemsSelectedPayload,
	RemoveCartPayload,
	SelectAllPayload,
	SelectProductPayload,
	SelectStorePayload,
	UPDATE_CART,
	UpdateCartPayload,
} from './InterfaceZustand';

// Actions are functions which update values in your store. These are static and never change, so they aren't technically "state".
// Organising them into a separate object in our store will allow us to expose them as a single hook to be used in any our component
// without any impact on performance:
interface Actions {
  fetchCartData: (options?: FetchCartOptions) => Promise<void>;
  fetchCartDataByRouter: (options?: FetchCartOptions) => Promise<void>;
  setSelectAll: (payload: SelectAllPayload, options?: Options) => Promise<void>;
  setSelectStore: (payload: SelectStorePayload, options?: Options) => Promise<void>;
  setSelectProduct: (payload: SelectProductPayload, options?: Options) => Promise<void>;
  setImportantProduct: (payload: ImportantProductPayload, options?: Options) => Promise<void>;
  // TODO: Function updateCartItem use for increase or decrease quantity of cart
  updateCartItem: (payload: UpdateCartPayload, options?: Options) => Promise<void>;
  removeCartItemsSelected: (payload: RemoveCartItemsSelectedPayload) => Promise<void>;
  removeCartItem: (payload: RemoveCartPayload, options?: Options) => Promise<void>;
  removeImportantProducts: (payload: { cartNo: string; skus: string[] }, options?: Options) => Promise<void>;
  setSelectedLists: (payload: any) => void;
  setFirstTimeGetCart: (status: boolean) => void;
  resetFirstTimeGetCart: () => void;
  updateRedeemApplyResult: (payload: any) => void;
  updatePromosRemoved: (payload: string) => void;
  resetPromosRemoved: () => void;
  updateNextUsingPromo: (
    payload: {
      code: string;
      paymentMethod: string;
      paymentMethodName: string;
    } | null,
  ) => void;

  getIDRef: (product: Partial<Product & ProductUpdate>) => string;
  getSKUfromIDRef: (idRef: string) => string;
  updateTotalQuantity: (totalQuantity: number) => void;
  updateTotalItems: (totalItems: number) => void;
  updateTriggerOpenAndScroll: () => void;
  clearTriggerOpenAndScroll: () => void;
  setLoadingSuggestVoucher: (type: 'PLATFORM' | 'SELLER' | 'PRODUCT', status: boolean) => void;
  // BV-2271: xếp hàng dòng giỏ đang hiện trên màn để hỏi SP hay mua kèm theo lô
  requestCrossSell: (item: { sku: string; productCode: string }) => void;
}

type InitialCartState = CartStore & {
  state: FetchStatus;
  // BV-2271: SP hay mua kèm theo skuCode dòng giỏ; [] = đã hỏi nhưng không có (không hỏi lại)
  crossSellBySku: { [sku: string]: CrossSellItem[] };
};
type CartState = InitialCartState & { actions: Actions };
type FetchCartOptions = { mode?: EnumUpdateCartMode; [k: string]: any };

// This enum was created to handle rules for sorting cartData when update quantity.
// Our rule for now: Backend will sort by the Newest SKU user added to cart. This case will happen when user F5 or first time go to Cart Page.
// But when user update quantity in cart page, we need to handle different case:
export enum EnumUpdateCartMode {
  // NO_SORTING mode use for update quantity in cart page => no need to sort cartData
  NO_SORTING = 'NO_SORTING',
  // PUSH_TO_TOP mode use for update quantity in cart page but in the "You May Like" block => need to push item to Top of block
  PUSH_TO_TOP = 'PUSH_TO_TOP',
  // DEFAULT_ACTION mode use for update quantity in other pages => use action base on data from API
  DEFAULT_ACTION = 'DEFAULT_ACTION',
}

const INITIAL_STATE: InitialCartState = {
  cartNo: '',
  cartData: [],
  cartDiscounts: [],
  cartFluctuates: [],
  cartErrors: [],
  selectedProducts: [],
  isOpenImportantProducts: false,
  isSelected: false,
  totalDiscount: 0,
  totalItems: 0,
  totalPrice: 0,
  totalItemsSelected: 0,
  totalQuantitySelected: 0,
  totalQuantity: 0,
  totalItemsImportantSelected: 0,
  price: 0,
  state: FETCH_STATUS.IDLE,
  hasDeal: false,
  selectedLists: {},
  isFirstTimeGetCart: true,
  redeemApplyResult: [],
  paymentMethod: '',
  redeemCode: [],
  oldCartItems: [],
  subPrice: 0,
  promosRemoved: '',
  nextUsingPromo: null,
  triggerOpenAndScroll: 0,
  voucherSuggestions: {},
  crossSellBySku: {},
  lastUpdatedIDRef: '',
  voucherSuggestLoading: {
    PLATFORM: false,
    PRODUCT: false,
    SELLER: false,
  },
};

const INITIAL_OPTIONS = {
  isReload: true,
  isNotify: true,
};

// BV-2271: hàng đợi cross-sell nằm ngoài store để không gây re-render. inFlight chỉ giữ sku đang chờ response;
// dedupe lâu dài dựa vào crossSellBySku nên khi store reset về INITIAL_STATE là tự hỏi lại được, không cần clear tay.
const CROSS_SELL_BATCH = 30;
const CROSS_SELL_DEBOUNCE_MS = 150; // các dòng cùng màn hình render gần như cùng tick, chỉ cần gom ngắn
const crossSellQueue = new Map<string, { sku: string; productCode: string }>();
const crossSellInFlight = new Set<string>();
let crossSellTimer: NodeJS.Timeout | undefined;

const useCartStates = create<CartState>()(
  devtools((set, get) => ({
    ...INITIAL_STATE,
    // ⬇️ separate "namespace" for actions
    actions: {
      fetchCartDataByRouter: async ({ mode }: FetchCartOptions = {}) => {
        const path = window.location.pathname;
        const {
          actions: { fetchCartData },
        } = get();

        // Cause in cart route, we need update all info relative to cart like: update voucher used, total price,... after we change quantity
        // => Need to call fetchCartData
        if (path === '/cart') {
          await fetchCartData({ mode });
          return;
        }

        updateInsiderCartInfo();

        // We only care about QUANTITY => no need call fetCartData to update all info relative to cart
        // => call cartLiteEncrypted is enough
        const res = await loadDataCartLiteEncrypted();
        if (res.status === 'OK') {
          set((_) => {
            const getFirstDataCart = getFirst(res);
            const cartStore: CartStore = convertCartStore(getFirstDataCart);

            const products = getFirstDataCart.cartItems.map((item: any) => ({
              ...item,
              skuCode: item.sku,
              quantityInCart: item.quantity,
            }));

            const cartStoreDto = {
              ...cartStore,
              cartData: [{ products }] as unknown as Seller[],
            };
            return {
              ...cartStoreDto,
              state: FETCH_STATUS.IDLE,
              isFirstTimeGetCart: false,
              totalQuantity: getFirst(res).totalQuantity,
            };
          });
          return;
        }
        set(() => ({ ...INITIAL_STATE, state: FETCH_STATUS.ERROR }));
      },
      // this method only call when we need update all info relative to cart info
      // avoid call it when only need renew quantity
      fetchCartData: async ({ mode }: FetchCartOptions = {}) => {
        updateInsiderCartInfo();

        // TODO: 9Jan25 - CUS_6215: Try using cartCache on voucherHelper, We would delay to fetch it make sure cartCahce is updated. Move it under and not affect to getCartBySeller
        try {
          const {
            cartData,
            lastUpdatedIDRef,
            isFirstTimeGetCart,
            promosRemoved,
            actions: { setLoadingSuggestVoucher },
          } = get();

          // if mode is undefined, we will sort cartData by default
          const onSort = typeof mode === 'undefined' ? true : mode === EnumUpdateCartMode.PUSH_TO_TOP;

          const res = await getCartBySeller(isFirstTimeGetCart, promosRemoved, onSort);
          const getFirstDataCart = getFirst(res);

          if (!getFirstDataCart) {
            set(() => ({ ...INITIAL_STATE, state: FETCH_STATUS.ERROR }));
            return;
          }

          const cartStore: CartStore = convertCartStore(getFirstDataCart);
          const oldCartItemsFormat = cartStore.cartData.map((item) =>
            item.products
              ?.filter((child) => child.isSelected)
              ?.map((product) => ({
                quantity: product.quantityInCart,
                price: product.noneVoucherPrice,
                total: Number(product.noneVoucherPrice) * Number(product.quantityInCart),
                sku: product.skuCode,
                sellerCode: product.sellerCode,
                productTags: product.productTags || [],
                productCode: product.productCode,
                isSelected: product.isSelected,
                storeCode: product.storeCode || '',
              })),
          );

          const newData = {
            // Change this line to update order of cartData
            ...cartStore,
            state: FETCH_STATUS.IDLE,
            isFirstTimeGetCart: false,
            oldCartItems: oldCartItemsFormat.flat() as IOldCartItem[],
            redeemCode: cartStore?.redeemApplyResult?.map((item: IRedeemApplyResult) => item.code),
          };

          set(() => newData);

          if (Router.pathname === CART_URL) {
            setLoadingSuggestVoucher('PLATFORM', Boolean(EnumVoucherSuggestStatus.LOADING));
            let voucherSuggestions: { [key: string]: IVoucherSuggestions } = {};
            getScreenCartVoucherHelper().then((res) => {
              if (isValid(res)) {
                res.data?.forEach((suggestion: { helpers: IVoucherSuggestions[] }) => {
                  suggestion?.helpers?.forEach((helper: IVoucherSuggestions) => {
                    switch (helper.suggestType) {
                      case 'PLATFORM':
                        voucherSuggestions['PLATFORM'] = helper;
                        break;
                      case 'PRODUCT':
                        if (helper?.skuCode) {
                          voucherSuggestions[helper.skuCode] = helper;
                        }
                        break;
                      default:
                        voucherSuggestions[helper.sellerCode as string] = helper; // SELLER
                        break;
                    }
                  });
                });
              } else {
                voucherSuggestions = {};
              }

              set((prev) => ({
                ...prev,
                voucherSuggestions,
              }));
              setLoadingSuggestVoucher('PLATFORM', Boolean(EnumVoucherSuggestStatus.DONE));
            });
          }
        } catch (error) {
          set(() => ({
            ...INITIAL_STATE,
            state: FETCH_STATUS.ERROR,
          }));
        }
      },
      setSelectAll: async (payload, options = INITIAL_OPTIONS) => {
        try {
          const {
            cartNo,
            actions: { fetchCartData },
          } = get();
          const { isReload } = options;
          const { isSelected } = payload;

          set((state) => ({
            ...state,
            isFirstTimeGetCart: true,
          }));

          const res = await CartClientV2.selectCartItemV2({
            isSelected,
            isAppliedAll: true,
          });

          if (!isValidWithoutData(res)) {
            NotifyUtils.error('Không thể chọn nhà bán hàng này, vui lòng thử lại');
          }

          if (isReload) {
            await fetchCartData({ mode: EnumUpdateCartMode.NO_SORTING });
          }
        } catch (error) {
          set((state) => ({
            ...state,
            state: FETCH_STATUS.ERROR,
          }));
        }
      },
      setSelectStore: async (payload, options = INITIAL_OPTIONS) => {
        try {
          const {
            actions: { fetchCartData },
          } = get();
          const { isSelected, skus, sellerGroup } = payload;
          const { isReload } = INITIAL_OPTIONS;

          set((state) => ({
            ...state,
            isFirstTimeGetCart: true,
          }));

          await CartClientV2.selectCartItemV2({ isSelected, skus });

          if (isReload) {
            await fetchCartData({ mode: EnumUpdateCartMode.NO_SORTING });
          }
        } catch (error) {
          set((state) => ({
            ...state,
            state: FETCH_STATUS.ERROR,
          }));
        }
      },
      setSelectProduct: async (payload, options = INITIAL_OPTIONS) => {
        try {
          const {
            actions: { fetchCartData },
          } = get();
          const { skuCode, isSelected } = payload;
          const { isReload } = options;

          set((state) => ({
            ...state,
            isFirstTimeGetCart: true,
          }));

          const res = await CartClientV2.selectCartItemV2({
            isSelected,
            sku: skuCode,
          });

          if (!isValid(res)) {
            NotifyUtils.error('Chọn sản phẩm không thành công, vui lòng chọn lại');
          }

          if (isReload) {
            await fetchCartData({ mode: EnumUpdateCartMode.NO_SORTING });
          }
        } catch (error) {
          set((state) => ({
            ...state,
            state: FETCH_STATUS.ERROR,
          }));
        }
      },
      setImportantProduct: async (payload, options = INITIAL_OPTIONS) => {
        try {
          const {
            actions: { fetchCartData },
          } = get();
          const { cartNo, sku, type, isImportant } = payload;
          const { isReload } = options;

          const res = await CartClientV2.updateCartItemImportant({
            cartNo,
            sku,
            type,
            isImportant,
            eventSource: mapSourceToEnum({}, window.location.pathname) || '',
            eventScreen: mapScreenToEnum({}, window.location.pathname) || '',
            host: window.location.host || '',
            source: 'thuocsi-web',
          } as CartParams & ImportantProductPayload);

          if (!isValidWithoutData(res)) {
            NotifyUtils.error('Đánh dấu quan trọng sản phẩm thất bại');
            return;
          }

          if (isReload) {
            await fetchCartData({ mode: EnumUpdateCartMode.NO_SORTING });
          }
          NotifyUtils.success('Đánh dấu quan trọng thành công');
        } catch (error) {
          set((state) => ({
            ...state,
            state: FETCH_STATUS.ERROR,
          }));
        }
      },
      updateCartItem: async (payload, options) => {
        try {
          const {
            cartNo,
            actions: { fetchCartDataByRouter },
          } = get();
          const { idRef, newQuantity, product, type } = payload;
          const { isNotify, isReload } = options || INITIAL_OPTIONS;
          const { metadata, mode, ...productWithoutMetadata } = product || {};

          const res = await CartClientV2.addCartItem({
            ...productWithoutMetadata,
            quantity: newQuantity,
            cartNo,
            page: window.location.pathname || 'home',
            searchKey: window.location.search,
            eventSource: product.eventSource || mapSourceToEnum(product, window.location.pathname) || '',
            eventScreen: mapScreenToEnum({}, window.location.pathname) || '',
            blockCode: undefined,
            metadata: {
              block_code: product?.blockCode,
              ...(metadata || {}),
            },
            host: window.location.host || '',
          } as CartParams & ProductUpdate);

          if (!isValid(res)) {
            NotifyUtils.error('Cập nhật SL không thành công: ' + res.message);
            return;
          }

          gtag.addToCart({ ...product, quantity: newQuantity || 0 });
          gtag.addToCartInPage({ ...product, quantity: newQuantity || 0 }, window.location.pathname);

          set((state) => ({
            ...state,
            lastUpdatedIDRef: idRef,
            isFirstTimeGetCart: true,
          }));
          //

          if (isReload) {
            setTimeout(() => {
              fetchCartDataByRouter({ mode });
            }, 0);
          }
          //
          if (isNotify) {
            const productName = capitalizeText(product.name);
            const message =
              type === UPDATE_CART.DECREASE ? `Số lượng sản phẩm ${productName} đã được giảm.` : `Số lượng sản phẩm ${productName} đã được tăng.`;

            NotifyUtils.success(message);
          }
        } catch (error) {
          set((state) => ({
            ...state,
            state: FETCH_STATUS.ERROR,
          }));
        }
      },
      removeCartItemsSelected: async (payload) => {
        try {
          const {
            cartNo,
            actions: { fetchCartDataByRouter },
          } = get();

          const { skus } = payload;

          set((state) => ({
            ...state,
            isFirstTimeGetCart: true,
          }));

          await CartClientV2.removeCartItem({
            cartNo,
            skus,
            source: 'thuocsi-web',
          });

          await fetchCartDataByRouter();
        } catch (error) {
          set((state) => ({
            ...state,
            state: FETCH_STATUS.ERROR,
          }));
        }
      },
      removeCartItem: async (payload, options = INITIAL_OPTIONS) => {
        try {
          const {
            cartNo,
            actions: { fetchCartDataByRouter },
          } = get();
          const { idRef, product } = payload;
          const { isNotify } = options;

          set((state) => ({
            ...state,
            lastUpdatedIDRef: idRef,
            isFirstTimeGetCart: true,
          }));

          const { metadata, ...productWithoutMetadata } = product || {};
          const res = await CartClientV2.removeCartItem({
            ...productWithoutMetadata,
            cartNo,
            page: window.location.pathname || 'home',
            searchKey: window.location.search,
            eventSource:
              product?.eventSource ||
              mapSourceToEnum(
                { isRecommended: false, isSameCategoryProduct: false, isHighProfit: product?.isHighProfit },
                window.location.pathname,
              ) ||
              '',
            eventScreen: mapScreenToEnum({ isRecommended: false, isSameCategoryProduct: false }, window.location.pathname) || '',
            host: window.location.host || '',
            metadata: {
              block_code: product?.blockCode,
              source:
                mapSourceToEnum(
                  { isRecommended: false, isSameCategoryProduct: false, isHighProfit: product?.isHighProfit },
                  window.location.pathname,
                ) || '',
              screen:
                mapScreenToEnum(
                  { isRecommended: false, isSameCategoryProduct: false, isHighProfit: product?.isHighProfit },
                  window.location.pathname,
                ) || '',
              ...(metadata || {}),
            },
            // blockCode: product?.blockCode,
          } as CartParams & ProductUpdate);

          if (!isValidWithoutData(res)) {
            NotifyUtils.error('Xoá sản phẩm thất bại');
            return;
          }

          if (isNotify) {
            NotifyUtils.success(`Sản phẩm ${capitalizeText(product.name)} đã được xóa ra khỏi giỏ hàng`);
          }

          await fetchCartDataByRouter();
        } catch (error) {
          set((state) => ({
            ...state,
            state: FETCH_STATUS.ERROR,
          }));
        }
      },
      removeImportantProducts: async (payload, options = INITIAL_OPTIONS) => {
        const {
          actions: { fetchCartDataByRouter },
        } = get();

        const res = await removeCartItemImportant({
          cartNo: payload.cartNo,
          skus: payload.skus,
        });

        const { isReload } = options;
        if (isReload) await fetchCartDataByRouter();
      },
      setSelectedLists: async (data) => {
        set(() => ({
          selectedLists: data,
        }));
      },
      setFirstTimeGetCart: (status: boolean) => {
        set((state) => ({
          ...state,
          isFirstTimeGetCart: status,
        }));
      },
      resetFirstTimeGetCart: () => {
        set((state) => ({
          ...state,
          isFirstTimeGetCart: true,
        }));
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      updateRedeemApplyResult: (newRedeemApplyResult: any) => {
        set((state) => ({
          ...state,
          redeemApplyResult: newRedeemApplyResult,
        }));
      },
      updatePromosRemoved: (promosRemoved: string) => {
        set((state) => ({
          ...state,
          promosRemoved,
        }));
      },
      resetPromosRemoved: () => {
        set((state) => ({
          ...state,
          promosRemoved: '',
        }));
      },
      updateNextUsingPromo: (nextUsingPromo) => {
        set((state) => ({
          ...state,
          nextUsingPromo,
        }));
      },
      updateTriggerOpenAndScroll: () => {
        set((state) => ({
          ...state,
          triggerOpenAndScroll: state.triggerOpenAndScroll + 1,
        }));
      },
      clearTriggerOpenAndScroll: () => {
        set((state) => ({
          ...state,
          triggerOpenAndScroll: 0,
        }));
      },

      getIDRef: (product: Partial<Product & ProductUpdate>) => {
        return `${Math.random()}::${product.skuCode || product.sku}`;
      },
      getSKUfromIDRef: (idRef: string) => {
        return (idRef || '').split('::')[1];
      },
      updateTotalQuantity: (totalQuantity: number) => {
        set((state) => ({
          ...state,
          totalQuantity,
        }));
      },
      updateTotalItems: (totalItems: number) => {
        set((state) => ({
          ...state,
          totalItems,
        }));
      },
      setLoadingSuggestVoucher(type, status) {
        set((state) => ({
          ...state,
          voucherSuggestLoading: {
            ...state.voucherSuggestLoading,
            [type]: status,
          },
        }));
      },
      // BV-2271: gom dòng đang hiện trên màn, debounce 150ms rồi gọi helper ≤30 dòng/lần; lỗi thì gán [] và không retry
      requestCrossSell: ({ sku, productCode }) => {
        if (Router.pathname !== CART_URL || crossSellInFlight.has(sku) || sku in get().crossSellBySku) return;
        crossSellQueue.set(sku, { sku, productCode });
        clearTimeout(crossSellTimer);
        crossSellTimer = setTimeout(async function flush() {
          const items = Array.from(crossSellQueue.values()).slice(0, CROSS_SELL_BATCH);
          if (!items.length) return;
          const batch: { [sku: string]: CrossSellItem[] } = {};
          items.forEach((item) => {
            crossSellQueue.delete(item.sku);
            crossSellInFlight.add(item.sku);
            batch[item.sku] = []; // không có trong response / lỗi → [] để không hỏi lại
          });
          try {
            const res = await getScreenCartCrossSellHelper(items);
            if (isValid(res)) {
              res.data?.forEach((row: { sku: string; crossSell: CrossSellItem[] }) => {
                batch[row.sku] = (row.crossSell || []).map(convertCrossSellItem);
              });
            }
          } catch (error) {
            // không retry
          }
          items.forEach((item) => crossSellInFlight.delete(item.sku));
          set((prev) => ({ crossSellBySku: { ...prev.crossSellBySku, ...batch } }));
          if (crossSellQueue.size) crossSellTimer = setTimeout(flush, CROSS_SELL_DEBOUNCE_MS);
        }, CROSS_SELL_DEBOUNCE_MS);
      },
    },
  })),
);

export const useCartActions = () => useCartStates((state) => state.actions);

export default useCartStates;
